import { useEffect, useRef, useState } from "react";
import { withClientContext } from "~/apollo/client";
import { DdqProdOpsWorkflowType } from "~/graphql/supergraphEnums";
import {
  useGetDdqExtractionDefaultsQuery,
  useGetDdqExtractionRunsQuery,
  useRunDdqExtractionMutation,
} from "~/graphql/supergraphHooks";
import type {
  GetDdqExtractionDefaultsQuery,
  ProcessingStatus,
} from "~/graphql/supergraphTypes";

export type ExtractionDefaults = GetDdqExtractionDefaultsQuery["ddqExtractionDefaults"];

export const RUN_POLL_INTERVAL_MS = 3000;

export const GENERIC_FAILURE_MESSAGE = "JunieAI couldn't structure the document. Try again.";

// Codegen types this field against a string union, not the supergraphEnums enum of the same name.
const IN_FLIGHT: ProcessingStatus[] = ["pending", "in_progress"];

export type UseStructureDdqRunResult = {
  defaults: ExtractionDefaults | undefined;
  isLoadingDefaults: boolean;
  isRunStateKnown: boolean;
  isRunning: boolean;
  failureMessage: string | null;
  startError: string | null;
  isStarting: boolean;
  run: (questionsPrompt: string) => void;
};

export type UseStructureDdqRunArgs = {
  arenaId: number;
  processTaskId: string;
  workflowType: DdqProdOpsWorkflowType;
  onStarted: () => void;
  onSucceeded: () => void;
};

// Run state is read back off the server rather than remembered locally, so a reload or a
// trip out of the wizard mid-run still lands on the loading state instead of the bar.
export const useStructureDdqRun = ({
  arenaId,
  processTaskId,
  workflowType,
  onStarted,
  onSucceeded,
}: UseStructureDdqRunArgs): UseStructureDdqRunResult => {
  const [startError, setStartError] = useState<string | null>(null);

  const { data: defaultsData, loading: isLoadingDefaults } = useGetDdqExtractionDefaultsQuery({
    context: withClientContext("federation"),
    variables: { targetType: workflowType },
  });

  const {
    data: runsData,
    refetch: refetchRuns,
    startPolling,
    stopPolling,
  } = useGetDdqExtractionRunsQuery({
    context: withClientContext("federation"),
    variables: { arenaId, prodOpsTaskId: processTaskId, limit: 1 },
    fetchPolicy: "cache-and-network",
  });

  const [latestRun] = runsData?.ddqExtractionRuns ?? [];
  const isRunning = latestRun != null && IN_FLIGHT.includes(latestRun.status);
  // Before the first read lands, a run may already be in flight — so "not running" is not yet a
  // fact, and the bar must not offer to start one.
  const isRunStateKnown = runsData != null;

  useEffect(() => {
    if (!isRunning) return;
    startPolling(RUN_POLL_INTERVAL_MS);
    return stopPolling;
  }, [isRunning, startPolling, stopPolling]);

  // Starts undefined so mounting straight into an already-succeeded run doesn't look like a transition.
  const previousStatusRef = useRef<ProcessingStatus | undefined>(undefined);
  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = latestRun?.status;
    const wasInFlight = previousStatus != null && IN_FLIGHT.includes(previousStatus);
    if (wasInFlight && latestRun?.status === "succeeded") {
      onSucceeded();
    }
  }, [latestRun?.status, onSucceeded]);

  const [runExtraction, { loading: isStarting }] = useRunDdqExtractionMutation({
    context: withClientContext("federation"),
    // The queue row's extractionStatus moves with the run.
    refetchQueries: ["GetDdqProdOpsQueue"],
  });

  // outlinePrompt is left off: the modal only exposes the questions prompt, so the server default applies.
  const run = (questionsPrompt: string) => {
    setStartError(null);
    runExtraction({
      variables: { input: { arenaId, prodOpsTaskId: processTaskId, questionsPrompt } },
    })
      .then(async () => {
        // Refetch before onStarted: closing the modal first hands back an enabled bar for the
        // length of a round trip, which is long enough to submit a second run.
        await refetchRuns();
        onStarted();
      })
      .catch((error: unknown) => {
        setStartError(error instanceof Error ? error.message : "Something went wrong.");
      });
  };

  return {
    defaults: defaultsData?.ddqExtractionDefaults,
    isLoadingDefaults,
    isRunStateKnown,
    isRunning,
    failureMessage:
      latestRun?.status === "failed" ? (latestRun.error ?? GENERIC_FAILURE_MESSAGE) : null,
    startError,
    isStarting,
    run,
  };
};
